/**
 * Normalise FacetWP's dropdown facets to the design's contract, then
 * (re-)initialise tom-select on them — on first render and after every
 * FacetWP refresh.
 *
 * FacetWP prints only the bare minimum its own JS needs:
 *
 *   <div class="facetwp-facet facetwp-facet-year facetwp-type-dropdown" data-name="year" data-type="dropdown">
 *     <select class="facetwp-dropdown">...</select>
 *   </div>
 *
 * _design/dist/projects.html (lines 615-650) expects a lot more, none of
 * which FacetWP ever prints on its own:
 *   - `tom-select` class, `autocomplete="off"` and `data-readonly` on the
 *     <select> (main.js only initialises `select.tom-select`, and without
 *     `data-readonly` the select would accept free-text search instead of
 *     forcing a pick from the list);
 *   - `placeholder` on the <select> (design duplicates it from the empty
 *     option's text — "Всі категорії", "Всі роки" — so it's read from
 *     there, never hardcoded);
 *   - a `check-sel`/`radio-sel` modifier and `data-label` on the wrapper
 *     `.facetwp-facet` — both are real, styled selectors in main.scss
 *     (`.facetwp-type-dropdown.check-sel .ts-dropdown .option:before` swaps
 *     the option's checkbox/radio icon; `.facetwp-type-dropdown:before {
 *     content: attr(data-label) }` prints the facet's caption above the
 *     dropdown), not decorative leftovers.
 *
 * Only the theme knows which slot (categories/year/…) is which and what
 * its modifier/label should be — that mapping is CPT-specific
 * (nmhg_archive_facets() in functions.php), so PHP hands it to this file as
 * `window.nmhg.archiveFacets`, keyed by the facet's own `data-name`
 * (NMHG_Archive_Page_Meta::enqueue_facet_ui_data(), app/Meta/archive-page.php).
 * Only present on archive pages where the registry actually has dropdown
 * slots — everywhere else `window.nmhg?.archiveFacets` is simply absent,
 * and the wrapper is left as FacetWP rendered it.
 *
 * The tom-select init config itself is duplicated from main.js on purpose
 * — it's the source of truth for this project's tom-select setup, so
 * appearance/behaviour must stay identical to every other `.tom-select` on
 * the site.
 *
 * `.facetwp-facet.facetwp-type-dropdown select.facetwp-dropdown:not(.tomselected)`
 * — `.tomselected` is the class tom-select itself adds to an initialised
 * <select>, so this both targets only fresh elements and prevents
 * double-initialisation on repeated refreshes. Normalisation (class/attr
 * writes) is safe to repeat regardless — `classList.add()`/`setAttribute()`
 * are idempotent — but is only ever reached for not-yet-initialised
 * selects anyway, since that's what the outer selector already filters to.
 *
 * Runs on both `DOMContentLoaded` and `facetwp-loaded`: FacetWP is
 * documented to fire `facetwp-loaded` once on first render too, but that
 * isn't something to take on faith without a live page to check against,
 * so DOMContentLoaded is a second, redundant trigger for the very first
 * paint. Firing both is harmless — whichever runs first does the real
 * work, the other finds only already-`.tomselected` elements and no-ops.
 *
 * Multi-select (`<select multiple>`, set per-facet in FacetWP's own admin,
 * not by this theme): tom-select detects it itself from the element, no
 * flag needed from us. Two things differ from single-select and are
 * branched on `select.multiple` below — see the inline comments at each:
 * (1) the init config (onFocus/onBlur only make sense for a single chosen
 * item); (2) which tom-select class marks a chosen option — `.selected`
 * (works in both modes; main.scss's checkmark icon is keyed off `.active`
 * instead, which only coincides with "chosen" in single-select because
 * tom-select re-highlights the current value as `.active` when the
 * dropdown reopens — in multi that's just whichever option is under the
 * pointer, not "which are chosen", so the icon needs the `.selected` case
 * mapped too — see custom.scss).
 */
import TomSelect from 'tom-select';

const FACET_DROPDOWN_SELECTOR = '.facetwp-facet.facetwp-type-dropdown select.facetwp-dropdown';

/**
 * Wrapper-level chrome (modifier class + caption) for every facet the
 * registry knows about, whatever widget FacetWP renders inside it.
 *
 * Deliberately not tied to the dropdown branch below: the facet type is
 * chosen per facet in FacetWP's own admin, and `fselect` (FacetWP's built-in
 * multi-select) renders `.fs-wrap` markup instead of a tom-select-able
 * `<select>`. Both still need the `check-sel` modifier and the `data-label`
 * caption, so this runs first and independently — a facet switched between
 * types in the admin keeps its caption either way.
 *
 * Idempotent: `classList.add`/`setAttribute` can repeat harmlessly, which
 * matters because FacetWP rebuilds facet markup on every refresh.
 */
function applyFacetChrome() {
  document.querySelectorAll('.facetwp-facet[data-name]').forEach((wrapper) => {
    const meta = window.nmhg?.archiveFacets?.[wrapper.dataset.name];

    if (!meta) {
      return;
    }

    if (meta.class) {
      wrapper.classList.add(meta.class);
    }

    if (meta.label) {
      wrapper.setAttribute('data-label', meta.label);
    }
  });
}

function normalizeFacetDropdown(select) {
  select.classList.add('tom-select');
  select.setAttribute('autocomplete', 'off');
  select.setAttribute('data-readonly', '');

  if (!select.hasAttribute('placeholder')) {
    const placeholderOption = select.querySelector('option[value=""]');

    if (placeholderOption) {
      select.setAttribute('placeholder', placeholderOption.textContent.trim());
    }
  }
}

function initFacetDropdowns() {
  applyFacetChrome();

  document
    .querySelectorAll(`${FACET_DROPDOWN_SELECTOR}:not(.tomselected)`)
    .forEach((select) => {
      normalizeFacetDropdown(select);

      const noResultsText = select.dataset.noresults || 'No results found';
      const isReadonly = select.hasAttribute('data-readonly');
      // FacetWP switches a dropdown facet to <select multiple> when the
      // admin sets it to multi-select — same markup contract otherwise, so
      // this is the only signal to branch on (no per-facet hardcoding).
      const isMulti = select.multiple;

      const config = {
        create: false,
        maxOptions: 1000,
        searchField: ['text'],
        sortField: { field: 'text', direction: 'asc' },
        render: {
          item(data, escape) {
            return select.dataset.txt
              ? `<div>${select.dataset.txt} ${escape(data.text)}</div>`
              : `<div>${escape(data.text)}</div>`;
          },
          no_results() {
            return `<div class="no-results">${noResultsText}</div>`;
          },
        },
        onDropdownOpen() {
          this.dropdown.classList.add('is-open');
        },
        onDropdownClose() {
          this.dropdown.classList.remove('is-open');
          if (!isReadonly) this.control_input.blur();
        },
        onInitialize() {
          if (isReadonly) this.control_input.setAttribute('readonly', 'readonly');

          // main.scss only styles `.ts-dropdown.single` (option icons,
          // hover state, scroll, box shadow — see comment above); tom-select
          // sets `.multi` instead of `.single` on both the wrapper and the
          // dropdown for a <select multiple> and never touches that class
          // again afterwards, so adding `single` here once is enough for
          // the lifetime of the instance. Reuses the design's existing
          // rules as-is instead of duplicating them under `.multi` in
          // custom.scss — safe here because main.scss's `.single`-scoped
          // dropdown-panel rules don't target anything vendor.scss's own
          // `.multi` rules also style (those are all on `.ts-control`, not
          // `.ts-dropdown`). The `.ts-control` box itself is handled
          // separately (see custom.scss) because there vendor.scss *does*
          // have a conflicting `.multi.has-items .ts-control` padding rule
          // with higher specificity than main.scss's `.single` one.
          if (isMulti) {
            this.dropdown.classList.add('single');
            this.wrapper.classList.add('single');
          }
        },
      };

      if (isMulti) {
        // tom-select drops chosen options out of the panel in multi mode
        // (`hideSelected` defaults to true there, false for single). The
        // whole point of the design's check-sel/radio-sel icons is that an
        // option stays listed and switches to its checked variant, so the
        // default has to be turned off — otherwise `.option.selected` never
        // exists to style (see custom.scss). Single-select keeps tom-select's
        // own default untouched.
        config.hideSelected = false;
      }

      // onFocus/onBlur hide/show "the" (querySelector — singular) selected
      // item while the control is focused, so the placeholder-like browse
      // state can show through. That only makes sense with exactly one
      // possible item; in multi mode several `.item` chips can be selected
      // at once, and hiding just the first one on focus is wrong, so these
      // are only attached for single-select — behaviour there is otherwise
      // byte-for-byte the same config as before this file learned about
      // `multiple`.
      if (!isMulti) {
        config.onFocus = function () {
          const item = this.control.querySelector('.item');
          if (item && !isReadonly) item.style.display = 'none';
        };
        config.onBlur = function () {
          const item = this.control.querySelector('.item');
          if (item && !isReadonly) item.style.display = '';
        };
      }

      // No tom-select plugins (e.g. `remove_button`) added: the design
      // never shows a multi-select state at all, so there's nothing to
      // match by adding one — vendor.scss's own default multi/chip styling
      // (colours included) is what renders until/unless design specifies
      // otherwise.
      new TomSelect(select, config);
    });
}

/**
 * Collapsed fSelect label when more than one value is picked.
 *
 * fSelect writes every chosen value into `.fs-label`, so the control grows
 * and re-flows as you filter. The design's control is a fixed-width box, so
 * anything past a single value collapses to one translated caption instead
 * ("Обрано декілька"), handed over as `window.nmhg.facetI18n.multiple`
 * (NMHG_Archive_Page_Meta::enqueue_facet_ui_data) — the string is registered
 * with Polylang, never hardcoded here.
 *
 * Only ever overwrites the >1 case: at 0 or 1 selections fSelect's own label
 * (placeholder / the single value) is already what the design wants, and it
 * rewrites the element itself on every pick, so there is nothing to restore.
 *
 * Runs after fSelect's own click handler — the listener is delegated on
 * `document`, so it fires on the way up, once fSelect has already updated
 * the label — and again after each FacetWP refresh rebuilds the markup.
 */
function updateFsLabels() {
  const text = window.nmhg?.facetI18n?.multiple;

  if (!text) {
    return;
  }

  document.querySelectorAll('.facetwp-facet .fs-wrap').forEach((wrap) => {
    const label = wrap.querySelector('.fs-label');

    if (label && wrap.querySelectorAll('.fs-option.selected').length > 1) {
      label.textContent = text;
    }
  });
}

/**
 * `.num-sel` badge next to the panel's "Сортування" trigger — how many
 * values are currently picked, as a bare number.
 *
 * Counted from `.fs-option.selected` rather than from
 * `.facetwp-selections`: the option classes flip on click, before FacetWP
 * has refreshed, so the badge tracks the panel while it's still open. Counts
 * individual values, not facets — two categories plus a year reads as 3.
 */
function updateFacetCount() {
  const counter = document.querySelector('.panel-filter__open .num-sel');

  if (!counter) {
    return;
  }

  counter.textContent = String(
    document.querySelectorAll('.panel-filter .facetwp-facet .fs-option.selected').length,
  );
}

/**
 * Empty state of the chosen-values box: placeholder text on the left and a
 * greyed-out reset on the right (custom.scss, `.panel-filter__box.is-empty`).
 *
 * Read from `.facetwp-selections` rather than from `.fs-option.selected`:
 * the box shows what FacetWP has applied, and an option clicked but not yet
 * refreshed has no chip there. FacetWP empties the container completely when
 * nothing is chosen, so "no `li`" is the whole test.
 *
 * The reset gets a real `disabled` too, not just the look — otherwise it
 * stays in the tab order and screen readers announce a live button that
 * does nothing. FacetWP only listens for clicks on it, so the attribute
 * breaks nothing and is lifted as soon as a value is chosen.
 */
function updateEmptyState() {
  document.querySelectorAll('.panel-filter__box').forEach((box) => {
    const isEmpty = !box.querySelector('.facetwp-selections li');

    box.classList.toggle('is-empty', isEmpty);
    box.querySelectorAll('.facetwp-reset').forEach((reset) => {
      reset.disabled = isEmpty;
    });
  });
}

function refreshFacetUi() {
  updateFsLabels();
  updateFacetCount();
  updateEmptyState();
}

document.addEventListener('DOMContentLoaded', initFacetDropdowns);
document.addEventListener('facetwp-loaded', initFacetDropdowns);

document.addEventListener('DOMContentLoaded', () => requestAnimationFrame(refreshFacetUi));
document.addEventListener('facetwp-loaded', () => requestAnimationFrame(refreshFacetUi));
document.addEventListener('click', (event) => {
  if (event.target.closest('.facetwp-facet .fs-wrap .fs-option')) {
    requestAnimationFrame(refreshFacetUi);
  }
});
